import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../Pipeline/Pipeline.module.scss';
import { useGetKpiSettingsQuery } from '../../../../redux/services/kpiSettingsByTypeAPI';
import { useProfileParams, isAISummariesAccessible } from '../../../../utils/common';
import InsightAI from '../../../common/InsightAI/InsightAI';
import { useGetBusinessPerformanceSummaryQuery } from '../../../../redux/services/businessPerformaceAPI';
import { useGetAGCAIDetailsMutation } from '../../../../redux/services/agcAIAPI';
import isEmpty from 'lodash/isEmpty';
import { normalizeMetricSourceData } from '../../../../utils/agcUtil';
import { Revenue, RevenueDetails } from './KPI/Revenue/Revenue';
import BusinessLine, { BusinessLineDetails } from './KPI/BusinessLine/BusinessLine';
import CategoryMix, { CategoryMixDetails } from './KPI/CategoryMix/CategoryMix';
import SubCategory, { SubCategoryDetails } from './KPI/SubCategory/SubCategory';
import TopProducts, { TopProductsDetails } from './KPI/TopProducts/TopProducts';
import InteractiveTileGrid, { TilePair } from '../common/InteractiveTileGrid/InteractiveTileGrid';
import ErrorInPageScreen from '../../../common/ErrorInPageScreen/ErrorInPageScreen';
import { isKpiInConfig } from '../../../../utils/agcUtil';
import { toTitleCase } from '../../../../utils/TextFormatter';

interface CategoriesProps {
  category?: string;
}

const Portfolio: React.FC<CategoriesProps> = () => {
  const { t } = useTranslation();
  const [portfolioKPIsCodelist, setPortfolioKPIsCodelist] = useState<{ code: string }[]>([]);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [aiContent, setAiContent] = useState<string>('');
  const hasRequestedRef = useRef(false);

  const {
    data: portfolioKpisResponse,
    isFetching: isFetchingKpiSettings,
    isError: isKpiSettingsError,
    refetch: refetchKpiSettings,
  } = useGetKpiSettingsQuery(
    {
      type: 'business-performance-portfolio',
      affiliateCode,
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: performanceSummaryResponse,
    isLoading: isLoadingSummary,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetBusinessPerformanceSummaryQuery(
    { affiliateCode, aboNumber, payload: { kpis: portfolioKPIsCodelist } },
    {
      refetchOnMountOrArgChange: true,
      skip: Number(affiliateCode) === 0 || aboNumber === 0 || isEmpty(portfolioKPIsCodelist),
    }
  );

  const [getAGCAIDetails, { data: aiDetailsResp }] = useGetAGCAIDetailsMutation();

  const loading = isLoadingSummary || isFetchingKpiSettings;
  const error = isKpiSettingsError || isSummaryError;

  // Safe retry handler - only refetch queries that have been started
  const handleRetry = () => {
    refetchKpiSettings();
    // Only refetch summary if the query was started (not skipped)
    if (!isEmpty(portfolioKPIsCodelist)) {
      refetchSummary();
    }
  };

  useEffect(() => {
    const response = portfolioKpisResponse?.body || [];
    if (!isEmpty(response)) {
      const kpiCodes = response.map((item: { code: string }) => ({
        code: item.code,
      }));
      setPortfolioKPIsCodelist(kpiCodes);
    }
  }, [portfolioKpisResponse]);

  // Fetch AI summary for portfolio category
  useEffect(() => {
    const canFetch =
      Number(affiliateCode) !== 0 && aboNumber !== 0 && !isEmpty(portfolioKPIsCodelist);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({
      affiliateCode,
      aboNumber,
      payload: { kpis: portfolioKPIsCodelist } as any,
    });
  }, [affiliateCode, aboNumber, portfolioKPIsCodelist, getAGCAIDetails]);

  // Extract AI content from response
  useEffect(() => {
    if (aiDetailsResp?.body) {
      const aiSummary = aiDetailsResp.body?.summary || '';
      setAiContent(aiSummary);
    }
  }, [aiDetailsResp]);

  const kpisData = performanceSummaryResponse?.body?.kpis || [];
  const summaryByCode = (code: string) => kpisData.find((k: any) => k.kpiCode === code);

  // Check if KPI code exists in config
  const configKpis = portfolioKpisResponse?.body || [];
  const isInConfig = (code: string) => isKpiInConfig(configKpis, code);

  // Extract individual KPI data from summary response
  const revenueSummary = summaryByCode('revenue');

  const businessLineKpi = summaryByCode('business_line');
  const businessLineSummary = normalizeMetricSourceData(businessLineKpi);

  const categoryMixKpi = summaryByCode('category');
  const categoryMixSummary = normalizeMetricSourceData(categoryMixKpi);

  const subCategoryKpi = summaryByCode('sub_category');
  const subCategorySummary = normalizeMetricSourceData(subCategoryKpi);

  const topProductsKpi = summaryByCode('top10Product');
  const topProductsSummary = normalizeMetricSourceData(
    topProductsKpi,
    (product) =>
      `${toTitleCase(product.keyName)} ${product.key !== 'OTHERS' ? `${product.key}` : ''}`
  );

  const allTiles = [
    isInConfig('revenue') && {
      id: 'revenue',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <Revenue summary={revenueSummary} loading={loading} />
      ),
      kpiDetailTile: <RevenueDetails />,
    },
    isInConfig('business_line') && {
      id: 'business_line',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <BusinessLine summary={businessLineSummary} loading={loading} />
      ),
      kpiDetailTile: <BusinessLineDetails kpiCode="business_line" />,
    },
    isInConfig('category') && {
      id: 'category',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <CategoryMix summary={categoryMixSummary} loading={loading} />
      ),
      kpiDetailTile: <CategoryMixDetails kpiCode="category" />,
    },
    isInConfig('sub_category') && {
      id: 'sub_category',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <SubCategory summary={subCategorySummary} loading={loading} />
      ),
      kpiDetailTile: <SubCategoryDetails kpiCode="sub_category" />,
    },
    isInConfig('top10Product') && {
      id: 'top10Product',
      kpiTile: error ? (
        <ErrorInPageScreen handleClickTryAgain={handleRetry} />
      ) : (
        <TopProducts summary={topProductsSummary} loading={loading} />
      ),
      kpiDetailTile: <TopProductsDetails kpiCode="top10Product" />,
    },
  ].filter(Boolean) as TilePair[];

  return (
    <div className={styles.pipeline_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t('portfolio_performance')}</div>
      </div>
      {isAISummariesAccessible() && (
        <InsightAI
          content={aiContent}
          className={styles.insight_ai}
          showDropdown={true}
          updateDate="08/19/2024"
          isDropdownEnabled={true}
          flowerIconSize={24}
          title={'ai_summary_portfolio'}
        />
      )}
      <InteractiveTileGrid className={styles.tiles_grid_container} tiles={allTiles} />
    </div>
  );
};

export default Portfolio;
